import { CLICK_IDS, type WidgetCoinBackTriggeredBy } from '@sunflower-games/analytics-events';
import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { CoinBackSteps } from '../../features/coinbackTutorial/constants';
import { analytics } from '../../services/analytics';
import type { EventAction, EventFeatureSubType } from '../../services/analytics/types';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { isCoinBackBoosterEventTrigger } from '../../utils/events';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { AnimationKeys } from '../animation/settings/config';
import type { EventTriggerPopupData } from '../event-trigger/types';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import { animationConfigs, coinbackBoosterAnimationConfigs } from './config';
import { coinBackMock } from './mock';
import type * as coinBackTypes from './types';

const USE_MOCK = false;

export class CoinBackStore implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this);

    this.coinBackFlag = resolveFlag('coin_back_feat_client', true);
    this.coinBackTutorialFlag = resolveFlag('coin_back_tutorial_feat', true);
    this.coinBackBoosterFlag = resolveFlag('coin_back_booster_feat', true);

    ldClient?.on('ready', () => {
      this.coinBackFlag = resolveFlag('coin_back_feat_client', true);
      this.coinBackTutorialFlag = resolveFlag('coin_back_tutorial_feat', true);
      this.coinBackBoosterFlag = resolveFlag('coin_back_booster_feat', true);
    });

    ldClient?.on('change', (changes) => {
      if ('coin_back_feat_client' in changes) {
        this.updateCoinBackFlag(changes.coin_back_feat_client.current);
      }

      if ('coin_back_tutorial_feat' in changes) {
        this.updateCoinBackTutorialFlag(changes.coin_back_tutorial_feat.current);
      }

      if ('coin_back_booster_feat' in changes) {
        this.updateBoosterFlag(changes.coin_back_booster_feat.current);
      }
    });
  }

  @observable
  public coinBackFlag: boolean = false;

  @observable
  public coinBackTutorialFlag: boolean = false;

  @observable
  public highResolutionAnimation: boolean = false;

  @observable
  public loading: boolean = false;

  @action
  public setLoading(loading: boolean) {
    this.loading = loading;
  }

  @observable
  public currentCoinBack: coinBackTypes.CoinBack | null = null;

  @observable
  public currentCoinBackBooster: coinBackTypes.CoinBackBooster | null = null;

  @action
  private setCurrentCoinBack(coinBack: coinBackTypes.CoinBack | null) {
    this.currentCoinBack = coinBack;
  }

  @action
  public setCurrentCoinBackBooster(coinBackBooster: coinBackTypes.CoinBackBooster | null) {
    this.currentCoinBackBooster = coinBackBooster;
  }

  @observable
  public coinBackBoosterFlag: boolean = false;

  @observable
  public coinBackHighResAnimation: boolean = false;

  @observable
  public showCoinBackElement: boolean = false;

  @observable
  public runningAnimation: AnimationKeys | null = null;

  @action
  setRunningAnimation = (key: AnimationKeys | null) => {
    this.runningAnimation = key;
  };

  @observable
  public isFirstFetch: boolean = true;

  @action
  setIsFirstFetch = (value: boolean) => {
    this.isFirstFetch = value;
  };

  @action
  updateCoinBackFlag(value: boolean) {
    this.coinBackFlag = value;
  }

  @action
  updateCoinBackTutorialFlag(value: boolean) {
    this.coinBackTutorialFlag = value;
  }

  @action
  updateBoosterFlag(value: boolean) {
    this.coinBackBoosterFlag = value;
  }

  @action
  updateShowCoinBackElement = (show: boolean) => {
    this.showCoinBackElement = show;
  };

  @computed get hasCoinsToCollect() {
    return !!this.currentCoinBack?.collectableGcAmount || !!this.currentCoinBack?.collectableScAmount;
  }

  @computed
  public get isNewPeriodWithCollectables() {
    return Boolean(this.currentCoinBack && !this.currentCoinBack.acknowledgedStart && this.hasCoinsToCollect);
  }

  public checkIfBoosterActive = (
    booster: coinBackTypes.CoinBackBooster | null,
  ): booster is coinBackTypes.CoinBackBooster => {
    return (
      this.coinBackBoosterFlag &&
      !!booster &&
      this.tutorialStep === CoinBackSteps.CLOSED &&
      dayjs().isBefore(dayjs(booster.endDate))
    );
  };

  public dialogTypes: FeatureManagerDialogType[] = ['CoinBack', 'CoinBackBooster'];

  public tutorialSteps: string[] = [];

  @observable
  public coinBackTutorial = {
    step: CoinBackSteps.CHECK_FEATURE,
    open: false,
  };

  @action
  public updateTutorialStep(step: number) {
    this.coinBackTutorial.step = step;
  }

  @action
  public updateTutorialOpen(open: boolean) {
    this.coinBackTutorial.open = open;
  }

  @computed
  public get isTutorialOpen() {
    return this.coinBackTutorial.open;
  }

  @computed
  public get tutorialStep() {
    const { finishedCoinBackTutorial } = this.rootStore.userStore.profile ?? {};

    if (finishedCoinBackTutorial || !this.coinBackFlag || !this.coinBackTutorialFlag) {
      return CoinBackSteps.CLOSED;
    }

    return this.coinBackTutorial.step;
  }

  public setTutorialNextStep() {
    this.updateTutorialStep(this.coinBackTutorial.step + 1);
    if (this.coinBackTutorial.step === CoinBackSteps.HOW_TO_EARN_POINTS) {
      this.openDialog('tutorial');
    } else if (this.coinBackTutorial.step === CoinBackSteps.REWARDS_CHECK_BALANCE) {
      this.closeDialog();
      this.rootStore.uiStore.setOpenFullPageDialog('rewards');
    } else if (this.coinBackTutorial.step === CoinBackSteps.CLAIM_KEEP_COINBACK) {
      this.rootStore.uiStore.closeFullPageDialog();
      this.openDialog('tutorial');
      this.sendAnalytics({
        event_action: 'impression',
        feature_sub_type: 'coinback_tutorial_end',
        triggered_by: 'tutorial_end',
      });
    }
  }

  public startTutorial() {
    if (!this.currentCoinBack) return;

    this.sendAnalytics({
      event_action: 'impression',
      feature_sub_type: 'coinback_tutorial_start',
      triggered_by: 'tutorial_start',
    });

    this.updateTutorialOpen(true);
    this.updateTutorialStep(CoinBackSteps.CHECK_FEATURE);
  }

  public startDialogs(_type?: FeatureManagerDialogType) {
    const { finishedCoinBackTutorial } = this.rootStore.userStore.profile ?? {};

    if (!finishedCoinBackTutorial && this.coinBackTutorialFlag) {
      this.startTutorial();
      return true;
    }

    if (!this.currentCoinBack) return false;

    const { openDialog: openBoosterDialog } = this.currentCoinBackBooster ?? { openDialog: false };

    if (this.isNewPeriodWithCollectables || (openBoosterDialog && this.isFirstFetch)) {
      this.showCoinBackDialog();
      this.setIsFirstFetch(false);
      return true;
    }

    return false;
  }

  private showCoinBackDialog() {
    this.openDialog('new_period');
    this.sendAnalytics({
      event_action: 'impression',
      feature_sub_type: 'coinback_choice',
      triggered_by: 'new_period',
    });
  }

  public async finishTutorial(claim = false) {
    const click_id = claim ? CLICK_IDS.LOBBY_PAGE.POP_UP.COIN_BACK.CLAIM : CLICK_IDS.LOBBY_PAGE.POP_UP.COIN_BACK.KEEP;

    this.sendAnalytics({
      event_action: 'click',
      click_id,
      feature_sub_type: 'coinback_choice',
      triggered_by: 'tutorial_end',
    });

    this.updateTutorialStep(CoinBackSteps.CLOSED);

    await postRequest('coin-back/finish-tutorial', { claim });

    void this.fetch();
    void this.rootStore.userStore.updateProfile({ finishedCoinBackTutorial: true });

    this.updateTutorialOpen(false);
  }

  private loadImages(coinback: coinBackTypes.CoinBack) {
    const { finishedCoinBackTutorial } = this.rootStore.userStore.profile ?? {};

    const isBoosterActive = this.checkIfBoosterActive(this.currentCoinBackBooster);
    let config;

    if (isBoosterActive && finishedCoinBackTutorial) {
      config = coinbackBoosterAnimationConfigs;
    } else {
      config = animationConfigs;
    }

    const updateAndLoadImages = (keys: AnimationKeys[]) => {
      keys.forEach((key) => {
        this.rootStore.animationStore.updateAnimationConfig(key, { ...config[key] });
        void this.rootStore.animationStore.loadImages(key);
      });
    };

    if (!finishedCoinBackTutorial) {
      updateAndLoadImages([AnimationKeys.SAFE_OPEN, AnimationKeys.SAFE_OPEN_KEEP, AnimationKeys.SAFE_OPEN_CLAIM]);
    }

    const hasCoinsToCollect = !!coinback.collectableGcAmount || !!coinback.collectableScAmount;
    const isNewPeriodWithCollectables = !coinback.acknowledgedStart && hasCoinsToCollect;

    if (isNewPeriodWithCollectables) {
      updateAndLoadImages([AnimationKeys.SAFE_OPEN, AnimationKeys.SAFE_OPEN_KEEP, AnimationKeys.SAFE_OPEN_CLAIM]);
    } else {
      if (hasCoinsToCollect) {
        updateAndLoadImages([AnimationKeys.SAFE_CLOSE, AnimationKeys.SAFE_CLOSE_CLAIM]);
      } else {
        updateAndLoadImages([AnimationKeys.SAFE_LOCKED]);
      }
    }
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (!this.coinBackFlag) {
      if (this.currentCoinBack) {
        this.setCurrentCoinBack(null);
      }
      return 'OFF';
    }

    try {
      this.setLoading(true);

      const coinBack = await this.fetchCoinBack();

      if (!coinBack) {
        return 'OFF';
      }

      this.loadImages(coinBack);

      return 'READY';
    } catch (error) {
      devError('Error fetching coin back', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  });

  public async claimCoinBack(): Promise<void> {
    try {
      if (!this.currentCoinBack) return;

      if (this.isTutorialOpen) {
        await this.finishTutorial(true);
      } else if (this.isNewPeriodWithCollectables) {
        await postRequest('coin-back/new-period-action', {
          claim: true,
        });
      } else {
        await postRequest('coin-back/claim');
      }
    } catch (error) {
      devError('Error claiming coin back', error);
    }
  }

  public async keepCoinBack(): Promise<void> {
    try {
      if (!this.currentCoinBack) return;

      await postRequest('coin-back/new-period-action', {
        claim: false,
      });
      await this.fetch();
    } catch (error) {
      devError('Error keeping coin back', error);
    }
  }

  public resetCoinBack() {
    this.setCurrentCoinBack(null);
    this.setCurrentCoinBackBooster(null);
    this.setIsFirstFetch(true);
    this.updateTutorialOpen(false);
    this.updateTutorialStep(CoinBackSteps.CLOSED);
    this.setRunningAnimation(null);
    this.updateShowCoinBackElement(false);
  }

  private async fetchCoinBack() {
    try {
      const res = USE_MOCK
        ? coinBackMock
        : await getRequest<{ data: { coinBackData: coinBackTypes.CoinBack | null } }>('coin-back/current');

      const coinBack = res.data.coinBackData;

      if (!coinBack) return;

      runInAction(() => {
        this.setCurrentCoinBack(coinBack);
      });

      runInAction(() => {
        const animationKey = this.getAnimationKey;
        if (!this.runningAnimation) {
          this.setRunningAnimation(animationKey);
        }
      });
      return coinBack;
    } catch (error) {
      devError('Error fetching coin back', error);
    }
  }

  public openDialog(triggeredBy: 'tutorial' | 'new_period' | 'click' | 'widget_click') {
    this.rootStore.uiStore.setOpenDialog({
      dialog: this.currentCoinBackBooster
        ? EnumOpenDialog.isOpenCoinBackBoosterDialog
        : EnumOpenDialog.isOpenCoinBackDialog,
      options: {
        shown_by: triggeredBy,
      },
    });
  }

  public closeDialog() {
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.featureManagerStore.onDialogFinished('CoinBack');
  }

  public get getAnimationKey() {
    const { finishedCoinBackTutorial } = this.rootStore.userStore.profile ?? {};
    const isInTutorial = !finishedCoinBackTutorial && this.coinBackTutorialFlag;

    if (isInTutorial) {
      return AnimationKeys.SAFE_OPEN;
    } else if (this.isNewPeriodWithCollectables) {
      return AnimationKeys.SAFE_OPEN;
    } else if (this.hasCoinsToCollect) {
      return AnimationKeys.SAFE_CLOSE;
    } else {
      return AnimationKeys.SAFE_LOCKED;
    }
  }

  @action
  public updateCoinBackBooster(coinBackBoosterEvent: EventTriggerPopupData | null, openDialog: boolean) {
    if (coinBackBoosterEvent && isCoinBackBoosterEventTrigger(coinBackBoosterEvent.benefit)) {
      let widgetImageUrl = '';
      if (coinBackBoosterEvent.benefit.widgetImageUrl) {
        widgetImageUrl = coinBackBoosterEvent.benefit.widgetImageUrl;
      } else if (this.currentCoinBack?.defaultBoosterWidgetImageUrl) {
        widgetImageUrl = this.currentCoinBack.defaultBoosterWidgetImageUrl;
      }

      const endDate = dayjs(coinBackBoosterEvent.expiredAt).toISOString();

      this.setCurrentCoinBackBooster({
        ...coinBackBoosterEvent.benefit,
        openDialog,
        endDate,
        widgetImageUrl,
      });
      if (this.currentCoinBack) {
        this.loadImages(this.currentCoinBack);
      }
    } else {
      this.setCurrentCoinBackBooster(null);
    }
  }

  public sendAnalytics(
    args: {
      event_action: EventAction;
      click_id?: string;
      feature_sub_type?: EventFeatureSubType;
      triggered_by?: WidgetCoinBackTriggeredBy;
      data?: Record<string, unknown>;
    },
    data?: Record<string, unknown>,
  ) {
    if (!this.currentCoinBack) return;

    const isBoosterActive = this.checkIfBoosterActive(this.currentCoinBackBooster);

    const defaultData = {
      unlocked_cc_amount: this.currentCoinBack.collectableGcAmount,
      unlocked_sc_amount: this.currentCoinBack.collectableScAmount,
      locked_cc_amount: this.currentCoinBack.lockedGcAmount,
      locked_sc_amount: this.currentCoinBack.lockedScAmount,
      booster_multiplier: isBoosterActive,
    };

    let feature_sub_type = args.feature_sub_type;

    if (!feature_sub_type) {
      if (this.isTutorialOpen) {
        feature_sub_type = 'coinback_tutorial';
      } else if (this.isNewPeriodWithCollectables) {
        feature_sub_type = 'coinback_new_period';
      } else if (this.checkIfBoosterActive(this.currentCoinBackBooster)) {
        feature_sub_type = 'coinback_booster';
      } else {
        feature_sub_type = 'coinback';
      }
    }

    const eventObject = {
      feature_type: 'coin_back',
      widget_sub_type: 'coin_back',
      feature_sub_type,
      ...args,
      ...data,
      ...defaultData,
    } as const;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    void analytics.sendEvent('widget', eventObject);
  }

  public clearCoinBack() {
    this.setRunningAnimation(null);
    this.updateShowCoinBackElement(false);
  }
}
